// src/prototype/components/AdvancedPersonalizationBand.tsx
// ============================================================
// ADVANCED PERSONALIZATION CONTAINER (COLLAPSIBLE)
// Part of Personalization — available to all plans (freemium included).
// Provides the "Manage Weights" card (links to /weights) and existing
// initiatives import.
// ============================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, ChevronUp, CheckCircle2, ArrowRight, Scale, Settings } from 'lucide-react';
import { InitiativesUploadBand } from './InitiativesUploadBand';
import { useEA } from '../contexts/EAContext';

interface AdvancedPersonalizationBandProps {
  className?: string;
  defaultExpanded?: boolean;
}

export function AdvancedPersonalizationBand({
  className = '',
  defaultExpanded = false,
}: AdvancedPersonalizationBandProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Get customization state from context for the header badge
  const { areCategoryWeightsCustomized, areMeasureWeightsCustomized } = useEA();
  const hasCustomizations = areCategoryWeightsCustomized || areMeasureWeightsCustomized;

  // ============================================================
  // NAVIGATE TO WEIGHTS PAGE
  // ============================================================
  const handleOpenWeightSettings = () => {
    navigate('/weights', {
      state: {
        returnTo: '/home',
        restoreScrollY: window.scrollY,
      }
    });
  };

  // ============================================================
  // FULL COLLAPSIBLE EXPERIENCE (all plans)
  // ============================================================
  return (
    <div className={`rounded-xl overflow-hidden transition-all duration-200 ${
      isExpanded 
        ? 'bg-[#F5F7FA] border border-[#e5e7eb]' 
        : 'bg-white border border-[#e5e7eb] hover:border-[#18365E]/20'
    } ${className}`}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-4 cursor-pointer transition-all duration-150 ease-out group ${
          isExpanded ? 'bg-white border-b border-[#e5e7eb]' : ''
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-150 ${
            isExpanded 
              ? 'bg-[#18365E] text-white' 
              : 'bg-[#18365E]/10 text-[#18365E] group-hover:bg-[#18365E]/15'
          }`}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-[#18365E]">
                Advanced Personalization
              </h2>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#18365E]/10 text-[#18365E]">
                Optional
              </span>
              {hasCustomizations && (
                <div className="flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Customized
                </div>
              )}
            </div>
            <p className="text-xs text-[#717182] mt-0.5">
              Fine-tune how DVA evaluates and ranks opportunities for your organization
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#18365E]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#717182] group-hover:text-[#18365E] transition-colors" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-3">
          {/* ============================================================ */}
          {/* MANAGE WEIGHTS CARD - Replaces embedded WeightingConfigurationBand */}
          {/* ============================================================ */}
          <div className="bg-white rounded-lg border border-[#e5e7eb] p-4 hover:border-[#18365E]/20 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#18365E]/10 flex items-center justify-center text-[#18365E] shrink-0">
                  <Scale className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[#18365E]">Manage Weights</h3>
                    {hasCustomizations && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                        Modified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#717182] mt-1 leading-relaxed">
                    Adjust category and measure weights to reflect your organization's priorities. 
                    Changes immediately affect how opportunities are ranked.
                  </p>
                  {/* Quick stats */}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[10px] text-[#717182] flex items-center gap-1">
                      <Settings className="w-3 h-3" />
                      6 categories
                    </span>
                    <span className="text-[10px] text-[#717182]">•</span>
                    <span className="text-[10px] text-[#717182]">
                      38 measures
                    </span>
                    {hasCustomizations && (
                      <>
                        <span className="text-[10px] text-[#717182]">•</span>
                        <span className="text-[10px] text-green-600 font-medium">
                          Custom weights active
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenWeightSettings();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#18365E] text-white rounded-lg text-xs font-semibold 
                           hover:bg-[#18365E]/90 transition-colors shrink-0 group"
              >
                Open Weight Settings
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
          
          {/* Band 2: Existing Initiatives Upload (unchanged) */}
          <InitiativesUploadBand />
        </div>
      )}
    </div>
  );
}

export default AdvancedPersonalizationBand;